import { isSuccess } from '@devexperts/remote-data-ts'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { SummaryCost } from '@/components/Reservation/Costs'

import { SummaryReservation } from '@/components/Reservation/List/index.tsx'
import { SpotType } from '@/models/cart.ts'
import { QuoteReservation } from '@/models/quote.ts'
import { appSlice } from '@/store/appSlice.ts'
import { BookingAvailability } from '@/store/bookingAvailabilitySlice.ts'
import { cartSlice } from '@/store/cartSlice'
import {
  selectTotal,
  selectTotalPrice,
  selectWeatherInsurancePrice,
} from '@/store/cartSlice/selectors/selectTotalPrice.ts'
import { LicenseBookingMode, licenseSlice } from '@/store/licenseSlice'
import { mapSelectors } from '@/store/mapSlice.ts'

import { getObjectsComparison } from '@/utils/getObjectsComparison.ts'
import { normalizeReservation } from '@/utils/normalizeReservation.ts'
import { normalizeSpot } from '@/utils/normalizeSpot.ts'
import { insertPeriodSlice } from '../store/insertPeriodSlice.ts'
import { getReservationSpotId } from '../utils/getReservationSpotId.ts'
import { useAppSelector } from './store.ts'
import { useCartSelectedProducts } from './useCartSelectedProducts.ts'
import { useCartSelectedServices } from './useCartSelectedServices.ts'
import { useMapSpotName } from './useMapSpotName.ts'

export const useCartRecapData = (isUsingCartInfoData?: boolean) => {
  const { t } = useTranslation()

  const license = useAppSelector(licenseSlice.selectors.license)
  const bookingMode = license?.bookingMode
  const insertPeriod = useAppSelector(insertPeriodSlice.selectors.period)
  const isKiosk = useAppSelector(appSlice.selectors.isKiosk)
  const bookingPeriod = useAppSelector(
    insertPeriodSlice.selectors.bookingPeriod
  )
  const cartDetail = useAppSelector(cartSlice.selectors.cartDetail)
  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)
  const cartDetailServices = useAppSelector(
    cartSlice.selectors.cartDetailServices
  )
  const cartDetailPrices = useAppSelector(cartSlice.selectors.cartDetailPrices)
  const selectedBookingType = useAppSelector(
    cartSlice.selectors.selectedBookingType
  )
  const isWeatherInsuranceAddedToCart = useAppSelector(
    cartSlice.selectors.isWeatherInsuranceAddedToCart
  )
  const quote = useAppSelector(cartSlice.selectors.quote)
  const quoteWithDiscount = useAppSelector(
    cartSlice.selectors.quoteWithDiscount
  )
  const showWeatherInsurancePrice =
    isWeatherInsuranceAddedToCart ||
    (isSuccess(cartDetail) && !!cartDetail.value.cart.weatherPolicy)

  const formattedCartInfoReservations = useAppSelector(
    cartSlice.selectors.cartDetailReservations
  )
  const totalPrice = useAppSelector(selectTotalPrice(isUsingCartInfoData))
  const total = useAppSelector(selectTotal(isUsingCartInfoData))
  const weatherInsurancePrice = useAppSelector(
    selectWeatherInsurancePrice(isUsingCartInfoData)
  )

  const reservations = isUsingCartInfoData
    ? formattedCartInfoReservations
    : cartReservations

  const selectedProducts = useCartSelectedProducts(reservations)

  const { reservationServices, promoService } =
    useReservationServicesWithPromo(reservations)

  const today = DateTime.now().toISODate()

  const isRefundable = useAppSelector(
    licenseSlice.selectors.isBookingRefundable
  )

  const isQuoteWithDiscount = isSuccess(quoteWithDiscount)
  const paymentLinkAmount = isSuccess(cartDetail)
    ? cartDetail.value.paymentLink?.amount
    : undefined

  const mapElements = useAppSelector(mapSelectors.mapData)?.elements
  const { getMapSpotName } = useMapSpotName()

  const FEE_SERVICE_IDS = [512, 1613]
  const summaryReservations = reservations.map<SummaryReservation>(
    (reservation, index) => {
      const spotId = getReservationSpotId(reservation)

      const spot = mapElements?.find((element) =>
        getObjectsComparison(
          normalizeSpot(element),
          normalizeReservation(reservation)
        )
      ) ?? {
        name: reservation.spotName,
        sectorId: reservation.sectorId,
        sectorName: reservation.sectorName,
        subType: reservation.spotType,
      }

      const title = getMapSpotName(spot, {
        shouldShowSpotName: isUsingCartInfoData
          ? reservation.shouldShowSpotName
          : bookingMode === LicenseBookingMode.MAP,
      })

      return {
        productsSelected: [
          ...selectedProducts
            .filter((r) => r.spotId === spotId)
            .map((product) => ({
              isExtra: false,
              isGift: false,
              name: product.name,
              quantity: product.quantity,
            })),
          ...(isUsingCartInfoData
            ? cartDetailServices
              .find((r) => r.spotId === spotId)
              ?.services.filter(
                (service) => !FEE_SERVICE_IDS.includes(service.service_id)
              )
              .map((service) => {
                const isPromo =
                    promoService &&
                    index === 0 &&
                    promoService.id === service.service_id
                return {
                  isExtra: true,
                  isGift: false,
                  name: service.service_name,
                  quantity: isPromo ? service.bought - 1 : service.bought,
                }
              })
              .filter((service) => service.quantity > 0) || []
            : reservationServices
              .filter((r) => r.spotId === spotId)
              .map((service) => ({
                ...service,
                isExtra: true,
              }))),
          ...(promoService && index === 0 ? [promoService] : []),
        ],
        spotType: reservation.spotType as SpotType,
        title: title,
      }
    }
  )

  const getFeesPrice = () => {
    if (isUsingCartInfoData && isSuccess(cartDetail)) {
      return cartDetail.value.cart.totalFeesValue
    }

    if (isQuoteWithDiscount) {
      return quoteWithDiscount.value.fees
    }

    if (isSuccess(quote)) {
      return quote.value.fees
    }

    return 0
  }

  const getServicesPrice = () => {
    if (isUsingCartInfoData) {
      return cartDetailPrices?.totalServicesValue || 0
    }

    return 0
  }

  const totalSpotPrice = isUsingCartInfoData
    ? cartDetailPrices?.totalSpotsValue || 0
    : totalPrice - getServicesPrice() - getFeesPrice()

  const servicesPrice = getServicesPrice()

  let discountPrice

  if (isUsingCartInfoData) {
    discountPrice = cartDetailPrices?.totalDiscountValue
  } else if (isQuoteWithDiscount) {
    discountPrice = quoteWithDiscount.value.discountValue
  }

  const hasDiscountApplied = isUsingCartInfoData
    ? cartDetailPrices && cartDetailPrices.totalDiscountValue > 0
    : isQuoteWithDiscount

  const fees = getFeesPrice()

  const costs: SummaryCost[] = [
    {
      name:
        selectedBookingType === BookingAvailability.TICKETS
          ? t('common.tickets')
          : t('common.spot', {
            count: cartReservations.length,
          }),
      price: totalSpotPrice + servicesPrice,
    },
    ...(showWeatherInsurancePrice
      ? [
          {
            name: t('extra.weatherInsurance.title'),
            price: weatherInsurancePrice,
          },
        ]
      : []),
    ...(fees > 0 && !isKiosk
      ? [
          {
            hasTooltip: true,
            name: t('common.serviceCosts'),
            price: fees,
          },
        ]
      : []),
    ...(hasDiscountApplied && typeof discountPrice === 'number'
      ? [
          {
            isDiscount: true,
            name: t('insertDataForm.promo.discountCode'),
            price: discountPrice,
          },
        ]
      : []),
  ]

  const deposit =
    paymentLinkAmount !== undefined && total - paymentLinkAmount > 0
      ? paymentLinkAmount
      : 0

  return {
    beachName: license?.name || '',
    bookingPeriod,
    costs,
    deposit,
    endDate: insertPeriod?.end || today,
    isRefundable: !!isRefundable,
    location: license?.beach.city || '',
    region: license?.beach.region || '',
    reservations: summaryReservations,
    startDate: insertPeriod?.start || today,
    total,
  }
}

function useReservationServicesWithPromo(reservations: QuoteReservation[]) {
  const promotionalService = useAppSelector(
    cartSlice.selectors.promotionalService
  )

  const reservationServices = useCartSelectedServices(reservations)

  if (!promotionalService) {
    return {
      promoService: null,
      reservationServices,
    }
  }

  // Finds the first reservation that has the same service id of the promotional service
  const sameServiceIndex = reservationServices.findIndex(
    (service) => service.id === promotionalService.id
  )

  if (sameServiceIndex > 0) {
    return {
      promoService: null,
      reservationServices: reservationServices.map((service, index) => {
        if (sameServiceIndex === index) {
          return {
            ...service,
            isExtra: true,
            isGift: true,
            quantity: service.quantity + 1,
          }
        }

        return service
      }),
    }
  }

  return {
    promoService: {
      id: promotionalService.id,
      isExtra: true,
      isGift: true,
      name: promotionalService.name,
      quantity: 1,
    },
    reservationServices,
  }
}
